import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Phone, 
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
  Clock,
  Users,
  AlertTriangle,
  Settings,
  User,
  Wifi,
  WifiOff,
  List,
  LayoutGrid,
  Moon,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import { API_URL } from '../utils/api';
const API = API_URL;

// Format seconds to MM:SS
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Get wait time color based on SLA threshold
const getWaitTimeColor = (waitSeconds, slaThreshold) => {
  const percentage = (waitSeconds / slaThreshold) * 100;
  if (percentage < 50) return "text-emerald-400";
  if (percentage < 80) return "text-amber-400";
  if (percentage < 100) return "text-orange-500";
  return "text-red-500";
};

// Get wait time background for alerts
const getWaitTimeBg = (waitSeconds, slaThreshold) => {
  if (waitSeconds >= slaThreshold) return "bg-red-500/20 border-red-500/50 animate-pulse";
  if (waitSeconds >= slaThreshold * 0.8) return "bg-orange-500/10 border-orange-500/30";
  return "bg-card border-border/50";
};

// Waiting Call Card Component
function WaitingCallCard({ call, slaThreshold, onLookup, extensions = [] }) {
  const [waitTime, setWaitTime] = useState(0);
  
  useEffect(() => {
    // Calculate initial wait time
    const startTime = new Date(call.start_time || call.received_at || call.created_at).getTime();
    const updateWaitTime = () => {
      const now = Date.now();
      setWaitTime(Math.floor((now - startTime) / 1000));
    };
    
    updateWaitTime();
    const interval = setInterval(updateWaitTime, 1000);
    return () => clearInterval(interval);
  }, [call.start_time, call.received_at, call.created_at]);

  const isOverSLA = waitTime >= slaThreshold;
  const ringingExtensions = call.ringing_extensions || [];
  
  // Build a map of extension numbers to names (fallback lookup)
  const extensionNameMap = {};
  extensions.forEach(ext => {
    if (ext.phone_number) extensionNameMap[ext.phone_number] = ext.name;
    if (ext.extension) extensionNameMap[ext.extension] = ext.name;
  });
  
  // Get display name for an extension - use provided name or lookup from contacts
  const getExtensionDisplay = (ext) => {
    // ext can be an object {number, name} or just a string (number)
    const number = typeof ext === 'object' ? ext.number : ext;
    const providedName = typeof ext === 'object' ? ext.name : null;
    const name = providedName || extensionNameMap[number];
    return { name, number };
  };
  
  return (
    <Card 
      className={`${getWaitTimeBg(waitTime, slaThreshold)} transition-all`}
      data-testid={`waiting-call-${call.call_id || call.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isOverSLA ? 'bg-red-500/20' : 'bg-primary/20'}`}>
              <PhoneIncoming className={`w-5 h-5 ${isOverSLA ? 'text-red-500' : 'text-primary'}`} />
            </div>
            <div>
              <p className="font-medium">{call.from_name || call.caller_name || 'Unknown Caller'}</p>
              <p className="text-sm text-muted-foreground font-mono">
                {call.from_number || call.caller_id || 'No Caller ID'}
              </p>
              {call.queue_name && (
                <Badge variant="outline" className="mt-1 text-xs">
                  {call.queue_name}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <div className={`text-2xl font-mono font-bold ${getWaitTimeColor(waitTime, slaThreshold)}`}>
              {formatDuration(waitTime)}
            </div>
            <p className="text-xs text-muted-foreground">waiting</p>
            {isOverSLA && (
              <Badge variant="destructive" className="mt-1 text-xs gap-1">
                <AlertTriangle className="w-3 h-3" />
                Over SLA
              </Badge>
            )}
          </div>
        </div>
        
        {/* Show ringing extensions */}
        {ringingExtensions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
            {ringingExtensions.map((ext, idx) => {
              const extInfo = getExtensionDisplay(ext);
              return (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <Phone className="w-3 h-3 text-amber-500 animate-pulse" />
                  {extInfo.name ? (
                    <>
                      <span className="text-foreground">{extInfo.name}</span>
                      <span className="text-muted-foreground font-mono">({extInfo.number})</span>
                    </>
                  ) : (
                    <span className="text-foreground font-mono">{extInfo.number}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Compact call row for grouped queue view
function QueuedCallRow({ call, slaThreshold }) {
  const [waitTime, setWaitTime] = useState(0);
  
  useEffect(() => {
    const startTime = new Date(call.start_time || call.received_at || call.created_at).getTime();
    const updateWaitTime = () => {
      const now = Date.now();
      setWaitTime(Math.floor((now - startTime) / 1000));
    };
    
    updateWaitTime();
    const interval = setInterval(updateWaitTime, 1000);
    return () => clearInterval(interval);
  }, [call.start_time, call.received_at, call.created_at]);

  const isOverSLA = waitTime >= slaThreshold;
  
  return (
    <div className={`flex items-center justify-between p-2 rounded-md ${isOverSLA ? 'bg-red-500/10' : 'bg-muted/50'}`}>
      <div className="flex items-center gap-2">
        <PhoneIncoming className={`w-4 h-4 ${isOverSLA ? 'text-red-500' : 'text-primary'}`} />
        <div>
          <p className="text-sm font-medium">{call.from_name || 'Unknown Caller'}</p>
          <p className="text-xs text-muted-foreground font-mono">{call.from_number}</p>
        </div>
      </div>
      <div className="text-right">
        <p className={`text-sm font-mono font-bold ${getWaitTimeColor(waitTime, slaThreshold)}`}>
          {formatDuration(waitTime)}
        </p>
        {isOverSLA && (
          <Badge variant="destructive" className="text-xs">SLA</Badge>
        )}
      </div>
    </div>
  );
}

// Queue Card Component for grouped view
function QueueCard({ queueName, queueNumber, calls, slaThreshold, hasAutoAttendant, isAfterHours, isMainLine, onToggleQueue, onToggleAA, onToggleAH, onToggleMain }) {
  const hasWaiting = calls.length > 0;
  
  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return "Unknown";
    if (phone.startsWith("64") && phone.length > 9) return "0" + phone.slice(2);
    return phone;
  };
  
  return (
    <Card
      className={`border-border/50 ${hasWaiting ? "ring-2 ring-amber-500/50" : ""} border-l-4 ${isMainLine ? "border-l-emerald-500" : "border-l-primary"}`}
      data-testid={`queue-card-${queueNumber}`}
    >
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <span className="truncate">{queueName}</span>
            {isMainLine && (
              <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-500">Main</Badge>
            )}
            {hasAutoAttendant && (
              <Badge variant="outline" className="text-xs border-cyan-500 text-cyan-500">AA</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasWaiting && (
              <Badge className="bg-amber-500 text-white">{calls.length} waiting</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground font-mono">
            {formatPhone(queueNumber)}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onToggleMain && onToggleMain(queueNumber, !isMainLine)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-400 transition-colors"
              title={isMainLine ? "Remove main line flag" : "Mark as main line (appears in both views)"}
              data-testid={`toggle-main-${queueNumber}`}
            >
              {isMainLine ? (
                <ToggleRight className="w-4 h-4 text-emerald-400" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              <span>Main</span>
            </button>
            <button
              onClick={() => onToggleAH && onToggleAH(queueNumber, !isAfterHours)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-400 transition-colors"
              title={isAfterHours ? "Remove after-hours flag" : "Mark as after-hours queue"}
              data-testid={`toggle-ah-${queueNumber}`}
            >
              {isAfterHours ? (
                <ToggleRight className="w-4 h-4 text-amber-400" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              <span>AH</span>
            </button>
            <button
              onClick={() => onToggleAA && onToggleAA(queueNumber, !hasAutoAttendant)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-cyan-400 transition-colors"
              title={hasAutoAttendant ? "Remove auto-attendant flag" : "Mark as having auto-attendant"}
              data-testid={`toggle-aa-${queueNumber}`}
            >
              {hasAutoAttendant ? (
                <ToggleRight className="w-4 h-4 text-cyan-400" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              <span>AA</span>
            </button>
            <button
              onClick={() => onToggleQueue && onToggleQueue(queueNumber, false)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              title="Remove from queues"
              data-testid={`toggle-queue-${queueNumber}`}
            >
              <ToggleRight className="w-4 h-4 text-primary" />
              <span>Queue</span>
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        {hasWaiting ? (
          <div className="space-y-2">
            {calls.map(call => (
              <QueuedCallRow 
                key={call.origin_id || call.call_id} 
                call={call} 
                slaThreshold={slaThreshold}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-3">No callers waiting</p>
        )}
      </CardContent>
    </Card>
  );
}

// Extension Status Card Component
function ExtensionCard({ extension, isOnline, isOnCall, currentCall, lookupContactName }) {
  // Real-time call duration counter
  const [callDuration, setCallDuration] = useState(0);
  
  useEffect(() => {
    if (!isOnCall || !currentCall) {
      setCallDuration(0);
      return;
    }
    
    // Calculate duration from call start time
    const startTime = new Date(currentCall.start_time || currentCall.received_at || currentCall.created_at).getTime();
    
    const updateDuration = () => {
      const now = Date.now();
      const duration = Math.floor((now - startTime) / 1000);
      // Only show positive durations, and cap at a reasonable max (4 hours)
      setCallDuration(duration > 0 && duration < 14400 ? duration : 0);
    };
    
    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [isOnCall, currentCall?.call_id, currentCall?.start_time, currentCall?.received_at]);

  const getStatusColor = () => {
    if (isOnCall) return "bg-red-500";
    if (isOnline) return "bg-emerald-500";
    return "bg-zinc-500";
  };
  
  const getStatusText = () => {
    if (isOnCall) return "On Call";
    if (isOnline) return "Logged In";
    return "Offline";
  };

  const getCardBorder = () => {
    if (isOnCall) return "border-red-500/30";
    if (isOnline) return "border-emerald-500/30";
    return "border-border/50";
  };

  // Get the remote party number and name (the other party, not this extension)
  const getRemoteParty = () => {
    if (!currentCall) return { number: 'Unknown', name: null };
    
    const extNumber = extension.phone_number || extension.extension;
    const toNum = currentCall.to_number || '';
    const fromNum = currentCall.from_number || '';
    
    let result = { number: 'Unknown', name: null };
    
    // If this extension is the "to" party, show the "from" party (caller)
    if (toNum.includes(extNumber) || extNumber.includes(toNum.slice(-4))) {
      result = {
        number: fromNum || currentCall.caller_id || currentCall.remote_party || 'Unknown',
        name: currentCall.from_name || currentCall.caller_name
      };
    }
    // If this extension is the "from" party, show the "to" party (called)
    else if (fromNum.includes(extNumber) || extNumber.includes(fromNum.slice(-4))) {
      result = {
        number: toNum || currentCall.remote_party || 'Unknown',
        name: currentCall.to_name
      };
    }
    // Fallback - try to determine which is the external number
    // Internal extensions typically start with the account prefix, external don't
    else {
      const toIsInternal = toNum.startsWith('8302');
      const fromIsInternal = fromNum.startsWith('8302');
      
      if (toIsInternal && !fromIsInternal) {
        result = { number: fromNum, name: currentCall.from_name };
      } else if (fromIsInternal && !toIsInternal) {
        result = { number: toNum, name: currentCall.to_name };
      } else {
        result = { 
          number: currentCall.remote_party || fromNum || toNum || 'Unknown',
          name: currentCall.from_name || currentCall.to_name
        };
      }
    }
    
    // If no name was found in call data, try looking up in contacts
    if (!result.name && result.number && result.number !== 'Unknown' && lookupContactName) {
      result.name = lookupContactName(result.number);
    }
    
    return result;
  };

  const remoteParty = isOnCall && currentCall ? getRemoteParty() : null;

  // Determine call direction relative to this extension
  const getCallDirection = () => {
    if (!currentCall) return null;
    if (currentCall.direction === 'inbound') return 'in';
    if (currentCall.direction === 'outbound') return 'out';
    const extNumber = extension.phone_number || extension.extension;
    const toNum = currentCall.to_number || '';
    if (toNum.includes(extNumber) || extNumber.includes(toNum.slice(-4))) return 'in';
    return 'out';
  };

  const callDirection = isOnCall ? getCallDirection() : null;

  return (
    <Card 
      className={`bg-card ${getCardBorder()} transition-all hover:border-primary/30`}
      data-testid={`extension-${extension.phone_number || extension.extension}`}
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {/* Avatar with status indicator */}
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
              {extension.profile_image ? (
                <img 
                  src={extension.profile_image} 
                  alt={extension.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-5 h-5 text-primary" />
              )}
            </div>
            {/* Status indicator - only show when on a call */}
            {isOnCall && (
              <span 
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background bg-red-500"
                title="On Call"
              />
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{extension.name || 'Unknown'}</p>
            <p className="text-xs text-muted-foreground font-mono">
              {extension.extension || extension.phone_number}
            </p>
          </div>
        </div>
        
        {/* Call info if on call */}
        {isOnCall && currentCall && remoteParty && (
          <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
            {/* Show the remote party number with direction icon */}
            <div className="flex items-center gap-1 text-xs">
              {callDirection === 'in' ? (
                <PhoneIncoming className="w-3 h-3 text-blue-400 flex-shrink-0" title="Incoming call" />
              ) : (
                <PhoneOutgoing className="w-3 h-3 text-amber-400 flex-shrink-0" title="Outgoing call" />
              )}
              <span className="text-foreground font-mono truncate">
                {remoteParty.number}
              </span>
            </div>
            {/* Show remote party name if available */}
            {remoteParty.name && (
              <p className="text-xs text-muted-foreground truncate pl-4">
                {remoteParty.name}
              </p>
            )}
            {/* Call duration - real-time counter */}
            <div className="flex justify-end">
              <span className="text-red-400 font-mono text-xs">
                {formatDuration(callDuration)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function QueueMonitorTest() {
  const { token, user } = useAuth();
  
  // State
  const [waitingCalls, setWaitingCalls] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [allContacts, setAllContacts] = useState([]); // All contacts for name lookup
  const [queues, setQueues] = useState([]); // List of all queues
  const [busyExtensions, setBusyExtensions] = useState(new Set());
  const [onlineExtensions, setOnlineExtensions] = useState(new Set());
  const [activeCalls, setActiveCalls] = useState({});
  const [slaThreshold, setSlaThreshold] = useState(20); // Default 20 seconds
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tempSla, setTempSla] = useState(20);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('queueMonitorViewMode') || 'flat';
    }
    return 'flat';
  });
  
  // Helper to look up contact name by phone number
  const lookupContactName = (phoneNumber) => {
    if (!phoneNumber) return null;
    // Normalize phone number for comparison (remove spaces, dashes, etc)
    const normalizeNum = (num) => (num || '').replace(/[\s\-\(\)]/g, '');
    const normalized = normalizeNum(phoneNumber);
    
    // Try exact match first
    let contact = allContacts.find(c => normalizeNum(c.phone_number) === normalized);
    if (contact?.name) return contact.name;
    
    // Try matching last 8-9 digits (handles country code differences)
    const last9 = normalized.slice(-9);
    const last8 = normalized.slice(-8);
    contact = allContacts.find(c => {
      const cNum = normalizeNum(c.phone_number);
      return cNum.endsWith(last9) || cNum.endsWith(last8) || 
             normalized.endsWith(cNum.slice(-9)) || normalized.endsWith(cNum.slice(-8));
    });
    
    return contact?.name || null;
  };
  
  // Save view mode preference when it changes
  useEffect(() => {
    localStorage.setItem('queueMonitorViewMode', viewMode);
  }, [viewMode]);
  
  // Stats
  const [stats, setStats] = useState({
    totalWaiting: 0,
    longestWait: 0,
    overSLA: 0,
    availableAgents: 0,
    busyAgents: 0
  });

  // Fetch data on mount and poll
  useEffect(() => {
    fetchAllData();
    fetchSlaSettings();
    
    // Poll every 3 seconds for real-time updates
    const pollInterval = setInterval(fetchAllData, 3000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Update stats when data changes
  useEffect(() => {
    const overSLACount = waitingCalls.filter(call => {
      const startTime = new Date(call.start_time || call.received_at || call.created_at).getTime();
      const waitTime = (Date.now() - startTime) / 1000;
      return waitTime >= slaThreshold;
    }).length;

    setStats({
      totalWaiting: waitingCalls.length,
      longestWait: waitingCalls.reduce((max, call) => {
        const startTime = new Date(call.start_time || call.received_at || call.created_at).getTime();
        const waitTime = (Date.now() - startTime) / 1000;
        return Math.max(max, waitTime);
      }, 0),
      overSLA: overSLACount,
      availableAgents: extensions.filter(e => 
        onlineExtensions.has(e.phone_number) && !busyExtensions.has(e.phone_number)
      ).length,
      busyAgents: extensions.filter(e => busyExtensions.has(e.phone_number)).length
    });
  }, [waitingCalls, extensions, busyExtensions, onlineExtensions, slaThreshold]);

  const fetchAllData = async () => {
    await Promise.all([
      fetchWaitingCalls(),
      fetchExtensions(),
      fetchBusyExtensions(),
      fetchOnlineExtensions(),
      fetchQueues()
    ]);
    setLoading(false);
  };

  const fetchQueues = async () => {
    try {
      // Use the simpler /queues endpoint that reads from local settings
      const res = await axios.get(`${API}/connectus/queues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueues(res.data.queues || []);
    } catch (error) {
      console.error("Failed to fetch queues:", error);
    }
  };

  // Toggle queue status for a line
  const handleToggleQueue = async (lineNumber, isQueue) => {
    try {
      await axios.post(`${API}/connectus/lines/set-queue`, 
        { service_name: lineNumber, is_queue: isQueue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isQueue ? `Added ${lineNumber} to queues` : `Removed ${lineNumber} from queues`);
      fetchQueues();
    } catch (error) {
      console.error("Failed to toggle queue:", error);
      toast.error("Failed to update queue status");
    }
  };

  // Toggle auto-attendant status for a line
  const handleToggleAA = async (lineNumber, hasAA) => {
    try {
      await axios.post(`${API}/connectus/lines/set-auto-attendant`, 
        { service_name: lineNumber, has_auto_attendant: hasAA },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(hasAA ? `Enabled AA for ${lineNumber}` : `Disabled AA for ${lineNumber}`);
      fetchQueues();
    } catch (error) {
      console.error("Failed to toggle AA:", error);
      toast.error("Failed to update auto-attendant status");
    }
  };

  // Toggle after-hours status for a line
  const handleToggleAH = async (lineNumber, isAfterHours) => {
    try {
      await axios.post(`${API}/connectus/lines/set-after-hours`, 
        { service_name: lineNumber, is_after_hours: isAfterHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isAfterHours ? `Marked ${lineNumber} as after-hours` : `Removed after-hours from ${lineNumber}`);
      fetchQueues();
    } catch (error) {
      console.error("Failed to toggle after-hours:", error);
      toast.error("Failed to update after-hours status");
    }
  };

  // Toggle main line status (main lines appear in both Queues and After Hours)
  const handleToggleMain = async (lineNumber, isMainLine) => {
    try {
      await axios.post(`${API}/connectus/lines/set-main-line`, 
        { service_name: lineNumber, is_main_line: isMainLine },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isMainLine ? `Marked ${lineNumber} as main line` : `Removed main line from ${lineNumber}`);
      fetchQueues();
    } catch (error) {
      console.error("Failed to toggle main line:", error);
      toast.error("Failed to update main line status");
    }
  };

  const fetchWaitingCalls = async () => {
    try {
      // Fetch active/ringing calls that haven't been answered yet
      const res = await axios.get(`${API}/webhooks/active-calls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to only INCOMING ringing/waiting calls
      // We only care about inbound calls that are waiting to be answered
      const waiting = (res.data.active_calls || []).filter(call => {
        const callState = call.state || call.status;
        const isWaiting = callState === 'ringing' || callState === 'queued' || callState === 'waiting';
        const isIncoming = call.direction === 'inbound';
        
        return isWaiting && isIncoming;
      });
      
      // Group waiting calls by origin_id to consolidate ring group calls
      // One incoming call ringing on multiple extensions should appear as ONE card
      const groupedByOrigin = {};
      waiting.forEach(call => {
        const originId = call.origin_id || call.call_id;
        if (!groupedByOrigin[originId]) {
          groupedByOrigin[originId] = {
            ...call,
            // Store both number and name for each ringing extension
            ringing_extensions: [{
              number: call.to_number,
              name: call.to_name
            }],
            ringing_count: 1
          };
        } else {
          // Same origin - add this extension to the list only if not already present
          const existingExtensions = groupedByOrigin[originId].ringing_extensions;
          const alreadyExists = existingExtensions.some(ext => ext.number === call.to_number);
          if (!alreadyExists) {
            existingExtensions.push({
              number: call.to_number,
              name: call.to_name
            });
          }
          groupedByOrigin[originId].ringing_count++;
          // Use earliest start_time
          const existingStart = new Date(groupedByOrigin[originId].start_time || groupedByOrigin[originId].received_at).getTime();
          const newStart = new Date(call.start_time || call.received_at).getTime();
          if (newStart < existingStart) {
            groupedByOrigin[originId].start_time = call.start_time;
            groupedByOrigin[originId].received_at = call.received_at;
          }
        }
      });
      
      const consolidatedWaiting = Object.values(groupedByOrigin);
      setWaitingCalls(consolidatedWaiting);
      
      // Build active calls map for extension display (check both state and status)
      const callMap = {};
      (res.data.active_calls || []).forEach(call => {
        const callState = call.state || call.status;
        if (callState === 'answered' || callState === 'connected' || callState === 'in-progress') {
          if (call.extension) callMap[call.extension] = call;
          if (call.to_number) callMap[call.to_number] = call;
          if (call.from_number) callMap[call.from_number] = call;
        }
      });
      setActiveCalls(callMap);
    } catch (error) {
      console.error("Failed to fetch waiting calls:", error);
    }
  };

  const fetchExtensions = async () => {
    try {
      // Get contacts and lines in parallel, merge to catch any missing entries
      const [contactsRes, linesRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/connectus/lines`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { Data: [] } }))
      ]);
      
      // Store all contacts for name lookup (used when displaying who's on a call)
      setAllContacts(contactsRes.data || []);
      
      // Filter contacts to internal ones for extension display
      const internal = (contactsRes.data || []).filter(c => 
        c.source === 'hero' || 
        c.source === 'hero_internal' || 
        c.tags?.includes('internal') ||
        c.presence === true
      );
      
      // Build a set of phone numbers already in internal contacts
      const knownNumbers = new Set(internal.map(c => c.phone_number).filter(Boolean));
      
      // Add lines (extensions/virtual) not already in contacts
      const lines = linesRes.data?.Data || linesRes.data?.data || [];
      const extraFromLines = lines
        .filter(l => {
          const num = l.Number || l.ServiceName;
          return (l.Type === 'extension' || l.Type === 'virtual') && num && !knownNumbers.has(num);
        })
        .map(l => {
          const num = l.Number || l.ServiceName;
          return {
            id: num,
            name: l.Name || l.DisplayName || num,
            phone_number: num,
            extension: l.Extension || num,
            source: 'hero_internal',
            type: l.Type
          };
        });
      
      setExtensions([...internal, ...extraFromLines]);
    } catch (error) {
      console.error("Failed to fetch extensions:", error);
    }
  };

  const fetchBusyExtensions = async () => {
    try {
      const res = await axios.get(`${API}/webhooks/active-calls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusyExtensions(new Set(res.data.busy_extensions || []));
    } catch (error) {
      console.error("Failed to fetch busy extensions:", error);
    }
  };

  const fetchOnlineExtensions = async () => {
    try {
      const res = await axios.get(`${API}/softphone/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineExtensions(new Set(res.data.online_extensions || []));
    } catch (error) {
      console.error("Failed to fetch online extensions:", error);
    }
  };

  const fetchSlaSettings = async () => {
    try {
      const res = await axios.get(`${API}/company/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.sla_threshold) {
        setSlaThreshold(res.data.sla_threshold);
        setTempSla(res.data.sla_threshold);
      }
    } catch (error) {
      console.error("Failed to fetch SLA settings:", error);
    }
  };

  const saveSlaSettings = async () => {
    try {
      await axios.put(`${API}/company/settings`, {
        sla_threshold: tempSla
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlaThreshold(tempSla);
      setSettingsOpen(false);
      toast.success(`SLA threshold updated to ${tempSla} seconds`);
    } catch (error) {
      console.error("Failed to save SLA settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const isExtensionOnline = (ext) => {
    return onlineExtensions.has(ext.phone_number) || onlineExtensions.has(ext.extension);
  };

  const isExtensionBusy = (ext) => {
    return busyExtensions.has(ext.phone_number) || busyExtensions.has(ext.extension);
  };

  const getExtensionCall = (ext) => {
    return activeCalls[ext.phone_number] || activeCalls[ext.extension];
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading queue monitor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="queue-monitor-test">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
            Queue Monitor
          </h1>
          <p className="text-muted-foreground text-sm">
            Real-time call queue and extension status
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setSettingsOpen(true)}
          className="gap-2"
          data-testid="sla-settings-btn"
        >
          <Settings className="w-4 h-4" />
          SLA: {slaThreshold}s
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className={`bg-card border-border/50 ${stats.totalWaiting > 0 ? 'border-amber-500/30' : ''}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{stats.totalWaiting}</p>
            <p className="text-xs text-muted-foreground">Calls Waiting</p>
          </CardContent>
        </Card>
        
        <Card className={`bg-card border-border/50 ${stats.overSLA > 0 ? 'border-red-500/30' : ''}`}>
          <CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${stats.overSLA > 0 ? 'text-red-500' : ''}`}>
              {stats.overSLA}
            </p>
            <p className="text-xs text-muted-foreground">Over SLA</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border/50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono">
              {formatDuration(stats.longestWait)}
            </p>
            <p className="text-xs text-muted-foreground">Longest Wait</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-emerald-500/30">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.availableAgents}</p>
            <p className="text-xs text-muted-foreground">Logged In</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-red-500/30">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.busyAgents}</p>
            <p className="text-xs text-muted-foreground">On Calls</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Waiting Calls Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <PhoneIncoming className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Incoming Calls</h2>
            {waitingCalls.length > 0 && (
              <Badge variant="secondary">{waitingCalls.length}</Badge>
            )}
            {/* View Mode Toggle */}
            <div className="ml-auto flex items-center gap-0.5 bg-muted/50 rounded-lg p-1 border border-border/50">
              <button
                onClick={() => setViewMode('flat')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'flat' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="Caller view"
                data-testid="view-mode-caller"
              >
                <List className="w-3.5 h-3.5" />
                <span>Caller</span>
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'grouped' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="Group by queue"
                data-testid="view-mode-queues"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                <span>Queues</span>
              </button>
              <button
                onClick={() => setViewMode('afterhours')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'afterhours' 
                    ? 'bg-amber-500 text-white shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title="After-hours queues"
                data-testid="view-mode-afterhours"
              >
                <Moon className="w-3.5 h-3.5" />
                <span>After Hours</span>
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            {(viewMode === 'grouped' || viewMode === 'afterhours') ? (
              // Queue card views - filter by after-hours status
              // Main lines (isMainLine) appear in BOTH views
              (() => {
                const isAfterHoursView = viewMode === 'afterhours';
                // Main lines appear in both views, regular queues filtered by AH flag
                const filteredQueues = queues.filter(q => {
                  if (q.isMainLine) return true; // Main lines always show in both views
                  return isAfterHoursView ? q.isAfterHours : !q.isAfterHours;
                });
                
                // Build a map of queue number to waiting calls
                const callsByQueue = {};
                waitingCalls.forEach(call => {
                  const ringingNumbers = (call.ringing_extensions || []).map(e => e.number);
                  
                  let matchedQueue = null;
                  for (const queue of filteredQueues) {
                    const queueNumber = queue.number;
                    if (ringingNumbers.includes(queueNumber) || 
                        call.to_number === queueNumber ||
                        call.origin_id?.includes(queueNumber)) {
                      matchedQueue = queueNumber;
                      break;
                    }
                  }
                  
                  if (!matchedQueue && ringingNumbers.length > 0) {
                    matchedQueue = ringingNumbers[0];
                  }
                  
                  if (matchedQueue) {
                    if (!callsByQueue[matchedQueue]) {
                      callsByQueue[matchedQueue] = [];
                    }
                    callsByQueue[matchedQueue].push(call);
                  }
                });
                
                if (filteredQueues.length === 0) {
                  return (
                    <Card className="bg-card border-border/50">
                      <CardContent className="p-8 text-center">
                        {isAfterHoursView ? (
                          <Moon className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                        ) : (
                          <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                        )}
                        <p className="text-muted-foreground">
                          {isAfterHoursView ? 'No after-hours queues configured' : 'No queues configured'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {isAfterHoursView 
                            ? 'Toggle the AH or Main switch on a queue card to see it here'
                            : 'Configure queue lines in settings to see them here'}
                        </p>
                      </CardContent>
                    </Card>
                  );
                }
                
                return filteredQueues.map(queue => {
                  const queueNumber = queue.number;
                  const queueName = queue.name || queueNumber;
                  const queueCalls = callsByQueue[queueNumber] || [];
                  
                  return (
                    <QueueCard 
                      key={queueNumber}
                      queueName={queueName}
                      queueNumber={queueNumber}
                      calls={queueCalls}
                      slaThreshold={slaThreshold}
                      hasAutoAttendant={queue.hasAutoAttendant}
                      isAfterHours={queue.isAfterHours}
                      isMainLine={queue.isMainLine}
                      onToggleQueue={handleToggleQueue}
                      onToggleAA={handleToggleAA}
                      onToggleAH={handleToggleAH}
                      onToggleMain={handleToggleMain}
                    />
                  );
                });
              })()
            ) : waitingCalls.length === 0 ? (
              <Card className="bg-card border-border/50">
                <CardContent className="p-8 text-center">
                  <Phone className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground">No calls waiting</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Incoming calls will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              // Flat/Caller view - individual call cards
              waitingCalls.map(call => (
                <WaitingCallCard 
                  key={call.origin_id || call.call_id || call.id} 
                  call={call} 
                  slaThreshold={slaThreshold}
                  extensions={extensions}
                />
              ))
            )}
          </div>
        </div>

        {/* Extensions Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Extensions</h2>
            <Badge variant="secondary">{extensions.length}</Badge>
            <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><PhoneIncoming className="w-3 h-3 text-blue-400" /> In</span>
              <span className="flex items-center gap-1"><PhoneOutgoing className="w-3 h-3 text-amber-400" /> Out</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {extensions.length === 0 ? (
              <Card className="bg-card border-border/50 col-span-full">
                <CardContent className="p-8 text-center">
                  <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground">No extensions found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sync contacts from PBX to see extensions
                  </p>
                </CardContent>
              </Card>
            ) : (
              // Sort: busy first, then online, then offline
              [...extensions]
                .sort((a, b) => {
                  const aScore = isExtensionBusy(a) ? 0 : isExtensionOnline(a) ? 1 : 2;
                  const bScore = isExtensionBusy(b) ? 0 : isExtensionOnline(b) ? 1 : 2;
                  return aScore - bScore;
                })
                .map(ext => (
                  <ExtensionCard
                    key={ext.id || ext.phone_number}
                    extension={ext}
                    isOnline={isExtensionOnline(ext)}
                    isOnCall={isExtensionBusy(ext)}
                    currentCall={getExtensionCall(ext)}
                    lookupContactName={lookupContactName}
                  />
                ))
            )}
          </div>
        </div>
      </div>

      {/* SLA Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              SLA Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sla-threshold">Answer Time Threshold (seconds)</Label>
              <Input
                id="sla-threshold"
                type="number"
                min="5"
                max="300"
                value={tempSla}
                onChange={(e) => setTempSla(parseInt(e.target.value) || 20)}
                data-testid="sla-threshold-input"
              />
              <p className="text-xs text-muted-foreground">
                Calls waiting longer than this will be highlighted as over SLA.
                Typical values: 15-30 seconds.
              </p>
            </div>
            
            <div className="pt-2 border-t">
              <p className="text-sm font-medium mb-2">Preview:</p>
              <div className="flex gap-2 text-xs">
                <span className="text-emerald-400">0-{Math.floor(tempSla * 0.5)}s OK</span>
                <span className="text-amber-400">{Math.floor(tempSla * 0.5)}-{Math.floor(tempSla * 0.8)}s Warning</span>
                <span className="text-orange-500">{Math.floor(tempSla * 0.8)}-{tempSla}s Critical</span>
                <span className="text-red-500">{tempSla}s+ Over SLA</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSlaSettings} data-testid="save-sla-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
